import { createSelector, OutputParametricSelector } from "reselect";
import { BetslipApplicationState } from "../../../store/state/BetslipApplicationState";
import { AlertType, AlertViewModel } from "@ppb/the-wall-common/types";
import { getSportsbookBettingImplyRunnerFailures } from "../../../store/state/sportsbook-betting/sportsbook-betting-selectors";
import { BettingState, RUNNER_FAILURE_CODES } from "@ppb/betslip-core";
import { i18n } from "../../../helpers/i18n";

type NotificationStrategy = {
  [failureCode: string]: AlertViewModel;
};

export const BUILD_A_BET_MISSING_SELECTIONS_ALERT_ID = "buildabet-missing-selections";

export const createNotificationsViewModelBuilder = (): OutputParametricSelector<
  BetslipApplicationState,
  string[],
  AlertViewModel[],
  (failures: BettingState.ImplyRunnerFailuresMap, runnerIds: string[]) => AlertViewModel[]
> =>
  createSelector(
    [getSportsbookBettingImplyRunnerFailures, (_: BetslipApplicationState, runnerIds: string[]) => runnerIds],
    (failures, runnerIds) => {
      const allFailures = runnerIds.reduce<RUNNER_FAILURE_CODES[]>((failureCodes, id) => {
        const runnerFailures = failures[id] || [];

        return [...failureCodes, ...runnerFailures.map(({ failureCode }) => failureCode)];
      }, []);

      const NOTIFICATION_STRATEGY: NotificationStrategy = {
        [RUNNER_FAILURE_CODES.INVALID_SGM_COMBINATION_SINGLE_ODDS]: {
          id: BUILD_A_BET_MISSING_SELECTIONS_ALERT_ID,
          type: AlertType.Info,
          message: i18n({ key: "I18N.BETSLIP.BET_BUILDER_NOTIFICATION" }),
          detail: i18n({ key: "I18N.BETSLIP.BET_BUILDER_NOTIFICATION_DETAILS" }),
        },
        [RUNNER_FAILURE_CODES.IMPOSSIBLE_SAME_MARKET_COMBINATION]: {
          type: AlertType.Warning,
          message: i18n({ key: "I18N.BETSLIP.NOT_COMBINABLE" }),
          detail: i18n({ key: "I18N.BETSLIP.NOT_COMBINABLE_DETAILS" }),
        },
        [RUNNER_FAILURE_CODES.INVALID_SGM_COMBINATION]: {
          type: AlertType.Warning,
          message: i18n({ key: "I18N.BETSLIP.NOT_COMBINABLE" }),
          detail: i18n({ key: "I18N.BETSLIP.NOT_COMBINABLE_SELECTIONS_DESCRIPTION" }),
        },
      };
      const allUniqueFailures = Array.from(new Set(allFailures)).filter(
        (failureCode) => !!NOTIFICATION_STRATEGY[failureCode],
      );

      return allUniqueFailures.map((failureCode) => NOTIFICATION_STRATEGY[failureCode]);
    },
  );
